import { MecSize, TechSpec } from '@adapters/bigcommerce/types/product';
import { BigCommerceLocaleString } from '@adapters/bigcommerce/types/locale';
import {
  RowEntity,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';
import { deriveSizeChartFromTechSpecs } from './deriveSizeChartFromTechSpecs';

/**
 * Units a data row can carry and still be rendered. `currentUnit` is always
 * seeded from the chart's own units, so any of these will display.
 *
 * Mirrors the row filter in `_components/Product/FindMySizeModal/SizeChartView`.
 */
const RENDERABLE_UNITS = ['', 'key.unit_type', 'key.unit_cm', 'key.unit_in'];

const isRenderableRow = (row: RowEntity, isBike: boolean): boolean =>
  // Bikes have no cm/in toggle, and SizeChartView's bike branch renders every
  // named row whatever its unit (standover in mm, reach in degrees).
  (isBike || RENDERABLE_UNITS.includes(row.unit)) &&
  (row.name?.length ?? 0) > 0;

/**
 * The cell values of each data row that SizeChartView will draw.
 *
 * Row 0 holds the column headers, never data. SizeChartView makes one column per
 * pair of header labels and one cell per pair of values, so values past the last
 * column are never shown.
 *
 * The S3 payload is untyped, so a cell can be a number or null however the
 * interface reads. Coerce rather than trust it; this runs in getServerSideProps,
 * where a throw is a 500 on the whole PDP.
 */
const getDisplayedValues = (
  row: RowEntity[] | null | undefined,
  isBike: boolean
): string[][] => {
  const [header, ...dataRows] = row ?? [];
  const columnCount = Math.ceil((header?.values?.value?.length ?? 0) / 2);

  return dataRows
    .filter((dataRow) => isRenderableRow(dataRow, isBike))
    .map((dataRow) =>
      (dataRow.values?.value ?? [])
        .slice(0, columnCount * 2)
        .map((value) =>
          String(value ?? '')
            .replace('key.', '')
            .trim()
        )
        .filter((value) => value.length > 0)
    );
};

/**
 * Whether a chart's rows will actually render.
 *
 * The "Size guide" CTA must be gated on this rather than on the presence of the
 * `size_chart` metafield: the metafield is a string that says a chart *should*
 * exist, and when the chart behind it is missing or empty the CTA opens onto
 * nothing (ED-390).
 */
export function hasRenderableSizeChartRows(
  row: RowEntity[] | null | undefined,
  isBike = false
): boolean {
  return getDisplayedValues(row, isBike).some((values) => values.length > 0);
}

export function hasRenderableSizeChart(
  measure: SizeChartInterface | null | undefined,
  isBike = false
): boolean {
  return hasRenderableSizeChartRows(measure?.tables?.table?.row, isBike);
}

/**
 * Whether a chart contains real measurements.
 *
 * Authored values are numeric ("48", "21.75"). Long-form prose pasted into the
 * size chart field to force the CTA to appear is not, so such a chart loses to a
 * chart derived from the tech specs.
 */
const hasNumericMeasurements = (
  measure: SizeChartInterface | null | undefined,
  isBike: boolean
): boolean =>
  getDisplayedValues(measure?.tables?.table?.row, isBike).some((values) =>
    values.some((value) => /^\d/.test(value))
  );

/**
 * Picks the chart to render for a product.
 *
 * 1. The authored chart, when it renders and holds real measurements.
 * 2. Otherwise a chart derived from the tech specs.
 * 3. Otherwise `null`, so the CTA is hidden rather than opening empty.
 *
 * Step 2 only displaces an authored chart that has no numeric values at all, and
 * only when a derived chart exists to replace it — so the worst case is trading
 * an alpha-only chart for one with real measurements.
 *
 * `sizeChartId` is the product's `size_chart` metafield (falling back to the
 * authored chart's own id, which is the same value). It decides which tech spec
 * a derived chart is built from — `skis` → Dimension, `helmets` → Circumference,
 * `pfd` → Chest circumference — so PIT controls the measurement by naming the
 * chart, rather than the code guessing.
 */
export function resolveSizeChart({
  authored,
  techSpecs,
  locale,
  sizeChartId,
  sizes,
  isBike = false,
}: {
  authored: SizeChartInterface | null | undefined;
  techSpecs: TechSpec[] | undefined | null;
  locale: BigCommerceLocaleString;
  sizeChartId?: string | null;
  sizes?: readonly MecSize[] | null;
  isBike?: boolean;
}): SizeChartInterface | null {
  if (
    hasRenderableSizeChart(authored, isBike) &&
    hasNumericMeasurements(authored, isBike)
  ) {
    return authored ?? null;
  }

  const derived = deriveSizeChartFromTechSpecs(
    techSpecs,
    locale,
    sizeChartId ?? authored?.id,
    sizes
  );

  if (derived) return derived;

  return hasRenderableSizeChart(authored, isBike) ? authored ?? null : null;
}

export default resolveSizeChart;
